import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CATEGORIES, PLATFORMS, CREATOR_TIERS, ACCOUNT_TYPES, LANGUAGES, COUNTRIES, BUDGET_RANGES, AGE_BRACKETS, formatMoney } from "@/lib/constants";
import {
  Users, Search, MapPin, BadgeCheck, X, Instagram, Youtube, Facebook, Linkedin, TrendingUp, ShieldCheck,
} from "lucide-react";
import { FollowerGrowthChart } from "@/components/FollowerGrowthChart";
import { CityDatalist } from "@/components/CityDatalist";

const ALL = "__all__";

const ownerVerified = (inf) => Object.entries(inf.social_verified || {}).filter(([, v]) => v?.owner_verified).map(([k]) => k);
const PNAME = { instagram: "Instagram", youtube: "YouTube", facebook: "Facebook" };

function OwnerBadge({ inf, testid }) {
  const list = ownerVerified(inf);
  if (!list.length) return null;
  return (
    <span title={`Owner-verified: ${list.map((k) => PNAME[k] || k).join(", ")}`} data-testid={testid}
      className="inline-flex items-center gap-1 rounded-full bg-neon-lime/15 border border-neon-lime/40 text-neon-lime px-2 py-0.5 text-[11px] font-bold">
      <ShieldCheck className="h-3 w-3" /> Owner-verified
    </span>
  );
}

const verifiedFollowers = (inf) => {
  const sv = inf.social_verified || {};
  const cands = [inf.followers, ...Object.values(sv).map((v) => v?.followers || v?.subscribers)].filter((x) => x);
  return cands.length ? Math.max(...cands) : null;
};

function InfluencerModal({ inf, onClose }) {
  if (!inf) return null;
  return (
    <Dialog open={!!inf} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface border-border max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="sr-only">Influencer profile</DialogTitle></DialogHeader>
        <div className="flex items-center gap-4">
          <img src={inf.picture} alt="" className="h-16 w-16 rounded-2xl object-cover bg-muted" />
          <div>
            <div className="flex items-center gap-2"><h3 className="font-heading font-bold text-2xl">{inf.name}</h3>{inf.verified && <BadgeCheck className="h-5 w-5 text-neon-cyan" />}</div>
            <OwnerBadge inf={inf} testid="modal-owner-verified-badge" />
            {inf.location && <p className="text-sm text-muted-foreground flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {inf.location}</p>}
          </div>
        </div>
        {inf.bio && <p className="text-sm text-muted-foreground mt-4">{inf.bio}</p>}
        <div className="grid grid-cols-3 gap-3 mt-5">
          <div className="rounded-xl border border-border bg-background p-3 text-center"><p className="font-heading font-black text-lg">{verifiedFollowers(inf) != null ? Number(verifiedFollowers(inf)).toLocaleString() : "—"}</p><p className="text-xs text-muted-foreground">Followers</p></div>
          <div className="rounded-xl border border-border bg-background p-3 text-center"><p className="font-heading font-black text-lg">{inf.engagement_rate ? `${inf.engagement_rate}%` : "—"}</p><p className="text-xs text-muted-foreground">Engagement</p></div>
          <div className="rounded-xl border border-border bg-background p-3 text-center"><p className="font-heading font-black text-lg">{inf.starting_rate ? formatMoney(inf.starting_rate, inf.currency) : "—"}</p><p className="text-xs text-muted-foreground">Per video</p></div>
        </div>
        {inf.creator_type && <Badge variant="outline" className="mt-4 border-neon-pink/30 text-neon-pink bg-neon-pink/10">{inf.creator_type}</Badge>}
        <FollowerGrowthChart userId={inf.user_id} />
        {inf.social_verified?.youtube && (() => {
          const yt = inf.social_verified.youtube;
          const fmt = (n) => (n == null ? "—" : Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(n));
          return (
            <div className="mt-4 rounded-xl border border-neon-lime/30 bg-neon-lime/5 p-3" data-testid="modal-youtube-verified">
              <div className="flex items-center gap-2 mb-2">
                <Youtube className="h-4 w-4 text-destructive" />
                <span className="text-sm font-semibold">{yt.title || "YouTube"}</span>
                <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-neon-lime"><BadgeCheck className="h-3.5 w-3.5" /> {yt.owner_verified ? "Owner-verified" : "API-verified"}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center"><p className="font-heading font-black text-base">{fmt(yt.subscribers)}</p><p className="text-[10px] text-muted-foreground">Subscribers</p></div>
                <div className="text-center"><p className="font-heading font-black text-base">{fmt(yt.views)}</p><p className="text-[10px] text-muted-foreground">Views</p></div>
                <div className="text-center"><p className="font-heading font-black text-base">{fmt(yt.videos)}</p><p className="text-[10px] text-muted-foreground">Videos</p></div>
              </div>
            </div>
          );
        })()}
        {inf.social_verified?.instagram && (() => {
          const ig = inf.social_verified.instagram;
          const fmt = (n) => (n == null ? "—" : Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(n));
          return (
            <div className="mt-3 rounded-xl border border-neon-pink/30 bg-neon-pink/5 p-3" data-testid="modal-instagram-verified">
              <div className="flex items-center gap-2 mb-2">
                {ig.thumbnail && <img src={ig.thumbnail} alt="" className="h-8 w-8 rounded-full object-cover border border-border" referrerPolicy="no-referrer" />}
                <span className="text-sm font-semibold">@{ig.username}</span>
                <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-neon-lime"><BadgeCheck className="h-3.5 w-3.5" /> {ig.owner_verified ? "Owner-verified" : "API-verified"}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center"><p className="font-heading font-black text-base">{fmt(ig.followers)}</p><p className="text-[10px] text-muted-foreground">Followers</p></div>
                <div className="text-center"><p className="font-heading font-black text-base">{fmt(ig.posts)}</p><p className="text-[10px] text-muted-foreground">Posts</p></div>
                <div className="text-center"><p className="font-heading font-black text-base">{fmt(ig.following)}</p><p className="text-[10px] text-muted-foreground">Following</p></div>
              </div>
            </div>
          );
        })()}
        {Object.entries(inf.social_verified || {}).filter(([k]) => !["instagram", "youtube"].includes(k)).map(([k, v]) => {
          const fmt = (n) => (n == null ? "—" : Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(n));
          const name = { tiktok: "TikTok", x: "X", twitch: "Twitch", pinterest: "Pinterest", facebook: "Facebook" }[k] || k;
          return (
            <div key={k} className="mt-3 rounded-xl border border-border bg-background p-3" data-testid={`modal-${k}-verified`}>
              <div className="flex items-center gap-2 mb-2">
                {v.thumbnail && <img src={v.thumbnail} alt="" className="h-8 w-8 rounded-full object-cover border border-border" referrerPolicy="no-referrer" />}
                <span className="text-sm font-semibold">{name} · {k === "facebook" ? v.username : `@${v.username}`}</span>
                <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-neon-lime"><BadgeCheck className="h-3.5 w-3.5" /> {v.owner_verified ? "Owner-verified" : "Public stats"}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="text-center"><p className="font-heading font-black text-base">{fmt(v.followers)}</p><p className="text-[10px] text-muted-foreground">Followers</p></div>
                <div className="text-center"><p className="font-heading font-black text-base">{fmt(k === "tiktok" || k === "facebook" ? v.likes : v.posts)}</p><p className="text-[10px] text-muted-foreground">{k === "tiktok" || k === "facebook" ? "Likes" : k === "pinterest" ? "Pins" : k === "x" ? "Posts" : "Videos"}</p></div>
              </div>
            </div>
          );
        })}
        {inf.showcase?.length > 0 && (
          <div className="mt-4" data-testid="modal-showcase">
            <p className="text-xs font-semibold text-muted-foreground mb-2">Content showcase</p>
            <div className="grid grid-cols-3 gap-2">
              {inf.showcase.slice(0, 6).map((it) => (
                <div key={it.id} className="rounded-lg overflow-hidden border border-border bg-background">
                  {it.thumbnail ? <img src={it.thumbnail} alt="" className="w-full h-20 object-cover" referrerPolicy="no-referrer" /> : <div className="w-full h-20 flex items-center justify-center text-[10px] text-muted-foreground">No preview</div>}
                  <p className="text-[9px] uppercase tracking-wide text-neon-pink font-bold px-1.5 py-1">{it.platform}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        {inf.categories?.length > 0 && <div className="flex flex-wrap gap-2 mt-4">{inf.categories.map((c) => <span key={c} className="text-xs px-2.5 py-1 rounded-full bg-background border border-border">{c}</span>)}</div>}
        {inf.content_specialties && <p className="text-sm mt-4"><span className="text-muted-foreground">Specialties: </span>{inf.content_specialties}</p>}
        {inf.audience_demographics && <p className="text-sm mt-1"><span className="text-muted-foreground">Audience: </span>{inf.audience_demographics}</p>}
      </DialogContent>
    </Dialog>
  );
}

export default function FindInfluencers() {
  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState(ALL);
  const [platform, setPlatform] = useState(ALL);
  const [creatorType, setCreatorType] = useState(ALL);
  const [accountType, setAccountType] = useState(ALL);
  const [language, setLanguage] = useState(ALL);
  const [country, setCountry] = useState(ALL);
  const [city, setCity] = useState("");
  const [budget, setBudget] = useState("0");
  const [age, setAge] = useState("0");

  const load = async () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (category !== ALL) params.category = category;
    if (platform !== ALL) params.platform = platform;
    if (creatorType !== ALL) params.creator_type = creatorType;
    if (accountType !== ALL) params.account_type = accountType;
    if (language !== ALL) params.language = language;
    if (country !== ALL) params.country = country;
    if (city) params.city = city;
    const br = BUDGET_RANGES[Number(budget)];
    if (br && br.min != null) params.min_rate = br.min;
    if (br && br.max != null) params.max_rate = br.max;
    const ag = AGE_BRACKETS[Number(age)];
    if (ag && ag.min != null) params.min_age = ag.min;
    if (ag && ag.max != null) params.max_age = ag.max;
    const res = await api.get("/influencers", { params });
    setInfluencers(res.data);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, platform, creatorType, accountType, language, country, budget, age]);
  useEffect(() => { const t = setTimeout(load, 400); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search, city]);

  const clear = () => { setSearch(""); setCategory(ALL); setPlatform(ALL); setCreatorType(ALL); setAccountType(ALL); setLanguage(ALL); setCountry(ALL); setCity(""); setBudget("0"); setAge("0"); };
  const hasFilters = search || category !== ALL || platform !== ALL || creatorType !== ALL || accountType !== ALL || language !== ALL || country !== ALL || city || budget !== "0" || age !== "0";

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-neon-pink mb-2">Discovery</p>
          <h1 className="font-heading font-black text-4xl tracking-tight flex items-center gap-3"><Users className="h-8 w-8 text-neon-lime" /> Find influencers</h1>
          <p className="text-muted-foreground mt-2">Search and filter creators by category, platform, tier and more.</p>
        </div>

        <div className="rounded-2xl border border-border bg-surface p-4 mb-6 space-y-3">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="influencer-search-input" placeholder="Search creators by name, bio or specialty…" className="bg-background border-border h-11 pl-9" />
            </div>
            {hasFilters && <button onClick={clear} data-testid="clear-influencer-filters-btn" className="flex items-center gap-1 text-sm text-brand-pink hover:opacity-70 px-3 shrink-0"><X className="h-4 w-4" /> Clear</button>}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <Select value={accountType} onValueChange={setAccountType}>
              <SelectTrigger data-testid="influencer-accounttype-filter" className="bg-background border-border h-11"><SelectValue placeholder="Account type" /></SelectTrigger>
              <SelectContent><SelectItem value={ALL}>All creator types</SelectItem>{ACCOUNT_TYPES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="influencer-category-filter" className="bg-background border-border h-11"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent><SelectItem value={ALL}>All categories</SelectItem>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger data-testid="influencer-platform-filter" className="bg-background border-border h-11"><SelectValue placeholder="Platform" /></SelectTrigger>
              <SelectContent><SelectItem value={ALL}>All platforms</SelectItem>{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={creatorType} onValueChange={setCreatorType}>
              <SelectTrigger data-testid="influencer-tier-filter" className="bg-background border-border h-11"><SelectValue placeholder="Tier" /></SelectTrigger>
              <SelectContent><SelectItem value={ALL}>All tiers</SelectItem>{CREATOR_TIERS.map((c) => <SelectItem key={c.name} value={c.name}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger data-testid="influencer-language-filter" className="bg-background border-border h-11"><SelectValue placeholder="Language" /></SelectTrigger>
              <SelectContent><SelectItem value={ALL}>All languages</SelectItem>{LANGUAGES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger data-testid="influencer-country-filter" className="bg-background border-border h-11"><SelectValue placeholder="Country" /></SelectTrigger>
              <SelectContent><SelectItem value={ALL}>All countries</SelectItem>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={city} onChange={(e) => setCity(e.target.value)} list="city-filter-options" autoComplete="off" data-testid="influencer-city-filter" placeholder="City" className="bg-background border-border h-11" />
            <CityDatalist id="city-filter-options" />
            <Select value={age} onValueChange={setAge}>
              <SelectTrigger data-testid="influencer-age-filter" className="bg-background border-border h-11"><SelectValue placeholder="Age" /></SelectTrigger>
              <SelectContent>{AGE_BRACKETS.map((a, i) => <SelectItem key={i} value={String(i)}>{a.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={budget} onValueChange={setBudget}>
              <SelectTrigger data-testid="influencer-budget-filter" className="bg-background border-border h-11"><SelectValue placeholder="Price / video" /></SelectTrigger>
              <SelectContent>{BUDGET_RANGES.map((b, i) => <SelectItem key={i} value={String(i)}>{b.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <p className="text-muted-foreground">Loading creators…</p>
        ) : influencers.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-surface p-12 text-center">
            <Users className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
            <p className="font-heading font-bold text-xl mb-2">No creators found</p>
            <p className="text-muted-foreground">Creators appear here once they complete their profile. Adjust filters to broaden your search.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {influencers.map((inf, i) => (
              <motion.button key={inf.user_id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                onClick={() => setSelected(inf)} data-testid={`influencer-card-${inf.user_id}`}
                className="text-left rounded-2xl border border-border bg-surface p-6 hover:bg-surfaceHover transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <img src={inf.picture} alt="" className="h-14 w-14 rounded-2xl object-cover bg-muted" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-1"><p className="font-heading font-bold truncate">{inf.name}</p>{inf.verified && <BadgeCheck className="h-4 w-4 text-neon-cyan shrink-0" />}</div>
                    <OwnerBadge inf={inf} testid={`owner-verified-badge-${inf.user_id}`} />
                    {inf.location && <p className="text-xs text-muted-foreground flex items-center gap-1 truncate"><MapPin className="h-3 w-3" /> {inf.location}</p>}
                  </div>
                </div>
                {inf.bio && <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{inf.bio}</p>}
                <div className="flex items-center gap-4 text-sm mb-4">
                  <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5 text-neon-lime" /> {verifiedFollowers(inf) != null ? Number(verifiedFollowers(inf)).toLocaleString() : "—"}</span>
                  <span className="flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5 text-neon-pink" /> {inf.engagement_rate ? `${inf.engagement_rate}%` : "—"}</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {inf.creator_type && <Badge variant="outline" className="border-neon-pink/30 text-neon-pink bg-neon-pink/10">{inf.creator_type}</Badge>}
                  {inf.account_type && inf.account_type !== "Influencer" && <Badge variant="outline" className="border-brand-orange/40 text-brand-orange bg-brand-orange/10">{inf.account_type}</Badge>}
                  {(inf.categories || []).slice(0, 2).map((c) => <span key={c} className="text-xs px-2 py-0.5 rounded-full bg-background border border-border text-muted-foreground">{c}</span>)}
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </div>
      <InfluencerModal inf={selected} onClose={() => setSelected(null)} />
    </AppShell>
  );
}
